/**
 * Single-flight flush of the offline playback-heartbeat queue.
 *
 * Without a lock, overlapping reconnect / low-memory / manual flush triggers can
 * read the same IndexedDB rows and POST duplicates before the first flush deletes them.
 *
 * TODO(idempotency): playback-heartbeat body schema does not yet accept a client
 * idempotency key. When the API adds one, stamp each queued item (or body) and send it
 * so retries/duplicates are safe even across process restarts. Do not change the server
 * contract from the player alone.
 */

import type { IStoredHeartbeat } from '@/services/DBService'

export type THeartbeatQueueFlushDeps = {
  getHeartbeatQueue: () => Promise<IStoredHeartbeat[]>
  /**
   * Send one queued body; resolve on success (ack), reject on failure.
   * Caller should already include retries if desired.
   */
  sendHeartbeat: (body: Record<string, unknown>) => Promise<void>
  deleteHeartbeats: (ids: string[]) => Promise<void>
  /** Gap between sends to avoid bursting a flaky reconnection (default 100 ms). */
  gapMs?: number
  onItemFailed?: (err: unknown, queueItemId: string) => void
  sleep?: (ms: number) => Promise<void>
}

export type THeartbeatQueueFlusher = {
  /** Request a flush; concurrent callers share one in-flight run. */
  flush: () => Promise<void>
  isInFlight: () => boolean
}

const defaultSleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

/**
 * Create a single-flight flusher. Items are deleted only after a successful send ack.
 * Failed items remain queued for a later flush.
 */
export function createHeartbeatQueueFlusher(deps: THeartbeatQueueFlushDeps): THeartbeatQueueFlusher {
  let inFlight: Promise<void> | null = null
  const sleep = deps.sleep ?? defaultSleep
  const gapMs = deps.gapMs ?? 100

  async function runFlush(): Promise<void> {
    const queue = await deps.getHeartbeatQueue()
    for (const item of queue) {
      try {
        await deps.sendHeartbeat(item.body)
        // Remove only after ack so a concurrent flush cannot re-read this id as pending.
        await deps.deleteHeartbeats([item.id])
      }
      catch (err) {
        deps.onItemFailed?.(err, item.id)
      }
      if (gapMs > 0) {
        await sleep(gapMs)
      }
    }
  }

  return {
    flush() {
      if (inFlight != null) {
        return inFlight
      }
      inFlight = Promise.resolve(runFlush())
        .catch(() => {
          /* per-item failures are logged via onItemFailed; keep coalesce clearing */
        })
        .finally(() => {
          inFlight = null
        })
      return inFlight
    },
    isInFlight() {
      return inFlight != null
    }
  }
}
